import { useQuery } from 'react-query';
import { performanceReportingApiUrl, fetcher, handleError, ServerErrorResponse } from 'data_sources/constants';
import { EIS_SCORABLE_FIELD_TYPES } from '../_types';
import type { EisConfigScoringTemplate, EisRuleConfigurationResponse } from '../_types';

const parsePredicateJson = (templates: EisConfigScoringTemplate[]): EisConfigScoringTemplate[] =>
  templates.map((template) => ({
    ...template,
    indicators: template.indicators.filter((ind) => (EIS_SCORABLE_FIELD_TYPES as readonly string[]).includes(ind.fieldType)).map((indicator) => ({
      ...indicator,
      scores: indicator.scores.map((score) => ({
        ...score,
        predicateJson:
          typeof score.predicateJson === 'string'
            ? (JSON.parse(score.predicateJson) as Record<string, unknown>)
            : score.predicateJson,
      })),
    })),
  }));

export const getEisRuleConfiguration = async (
  ruleId: number,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void,
): Promise<EisRuleConfigurationResponse> => {
  try {
    const url = `${performanceReportingApiUrl()}/performance/eis/v1/rules/${ruleId}/configuration`;

    const response = await fetcher({
      url,
      method: 'GET',
      options: { signal },
    });

    if (response.errorDetails) {
      if (onError) onError(response);
      throw new Error(JSON.stringify(response));
    }

    return {
      ...response,
      scoring: {
        ...response.scoring,
        templates: parsePredicateJson(response.scoring?.templates ?? []),
      },
    };
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    } else if (onError && error instanceof Error) {
      handleError(error, { onError });
    }
    throw error;
  }
};

export const useEisRuleConfiguration = (
  ruleId: number,
  options = {},
  onError?: (errors: ServerErrorResponse) => void,
) => {
  return useQuery({
    queryKey: ['eisRuleConfiguration', ruleId],
    queryFn: ({ signal }) => getEisRuleConfiguration(ruleId, signal, onError),
    enabled: !!ruleId,
    retry: (failureCount, error) => {
      const statusCode = (error as any)?.status || (error as any)?.response?.status;
      if (statusCode >= 400) return false;
      return failureCount < 3;
    },
    ...options,
  });
};
